import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { systemReportsAPI } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import {
  Bell, Check, X, AlertTriangle, Info, FileText, Users, Shield,
  Loader2, ArrowLeft, CheckCircle, Clock, Filter
} from 'lucide-react';

const AlertsPage = () => {
  const navigate = useNavigate();
  const { notify } = useNotification();
  
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, critical, warning

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await systemReportsAPI.getAlerts();
      setAlerts(response.data);
    } catch (error) {
      notify.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (alertId) => {
    try {
      await systemReportsAPI.markAlertRead(alertId);
      setAlerts(prev => prev.map(a => 
        a.id === alertId ? { ...a, isRead: true } : a
      ));
    } catch (error) {
      notify.error('Failed to mark alert as read');
    }
  };

  const handleDismiss = async (alertId) => {
    try {
      await systemReportsAPI.dismissAlert(alertId);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      notify.success('Alert dismissed');
    } catch (error) {
      notify.error('Failed to dismiss alert');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await systemReportsAPI.markAllAlertsRead();
      setAlerts(prev => prev.map(a => ({ ...a, isRead: true })));
      notify.success('All alerts marked as read');
    } catch (error) {
      notify.error('Failed to mark alerts as read');
    }
  };

  const getAlertIcon = (type, severity) => {
    if (severity === 'critical') return <AlertTriangle className="w-5 h-5 text-red-400" />;
    if (severity === 'warning') return <AlertTriangle className="w-5 h-5 text-amber-400" />;
    if (type === 'report_ready') return <FileText className="w-5 h-5 text-violet-400" />;
    if (type.includes('vendor')) return <Users className="w-5 h-5 text-blue-400" />;
    if (type.includes('breach')) return <Shield className="w-5 h-5 text-red-400" />;
    return <Info className="w-5 h-5 text-blue-400" />;
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      info: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    return colors[severity] || colors.info;
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'unread') return !alert.isRead;
    if (filter === 'critical') return alert.severity === 'critical';
    if (filter === 'warning') return alert.severity === 'warning';
    return true;
  });

  const unreadCount = alerts.filter(a => !a.isRead).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-violet-950">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-violet-500/20">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Bell className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">System Alerts</h1>
                  <p className="text-violet-300 text-sm">
                    {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
                  </p>
                </div>
              </div>
            </div>
            
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-2 px-4 py-2 text-sm text-violet-300 hover:bg-violet-500/10 rounded-lg transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                Mark all read
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Filters */}
        <div className="flex items-center gap-2 mb-6">
          <Filter className="w-4 h-4 text-slate-400" />
          {['all', 'unread', 'critical', 'warning'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                filter === f
                  ? 'bg-violet-600 text-white'
                  : 'text-slate-400 hover:bg-slate-700'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'unread' && unreadCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Alerts List */}
        {filteredAlerts.length === 0 ? (
          <div className="text-center py-16">
            <Bell className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h2 className="text-lg text-white mb-2">No alerts</h2>
            <p className="text-slate-400">
              {filter === 'all' 
                ? "You're all caught up! No system alerts at this time."
                : `No ${filter} alerts to show.`}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`bg-slate-800/50 border rounded-xl p-4 transition-all ${
                  alert.isRead 
                    ? 'border-slate-700 opacity-75' 
                    : 'border-violet-500/30 shadow-lg shadow-violet-500/5'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${
                    alert.severity === 'critical' ? 'bg-red-500/20' :
                    alert.severity === 'warning' ? 'bg-amber-500/20' : 'bg-blue-500/20'
                  }`}>
                    {getAlertIcon(alert.type, alert.severity)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-medium ${alert.isRead ? 'text-slate-300' : 'text-white'}`}>
                            {alert.title}
                          </h3>
                          {!alert.isRead && (
                            <span className="w-2 h-2 bg-violet-500 rounded-full"></span>
                          )}
                        </div>
                        <p className="text-slate-400 text-sm">{alert.message}</p>
                      </div>
                      
                      <span className={`px-2 py-0.5 text-xs rounded-full border ${getSeverityBadge(alert.severity)}`}>
                        {alert.severity}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        {new Date(alert.createdAt).toLocaleString()}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {alert.link && (
                          <button
                            onClick={() => navigate(alert.link)}
                            className="px-3 py-1 text-sm text-violet-400 hover:bg-violet-500/10 rounded-lg transition-colors"
                          >
                            View
                          </button>
                        )}
                        {!alert.isRead && (
                          <button
                            onClick={() => handleMarkRead(alert.id)}
                            className="p-1 text-slate-400 hover:text-green-400 hover:bg-green-500/10 rounded transition-colors"
                            title="Mark as read"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDismiss(alert.id)}
                          className="p-1 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                          title="Dismiss"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsPage;
